import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select, Modal } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment';
import { getAllOrdersAction, getAllRidersAction, getAllSubscriptionAction, updateRiderStatusAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import styled from 'styled-components';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;
const { Option } = Select;

export const BasicTable: React.FC = () => {
  const [tableKey, setTableKey] = useState(0);
  const [filters, setFilters] = useState({
    clientName: '',
    deliveryDateRange: [],
    deliveryStatus: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const dispatch = useAppDispatch();
  let navigate = useNavigate();

  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      deliveryDateRange: filters.deliveryDateRange.length
        ? [
          moment(filters.deliveryDateRange[0]).toISOString(),
          moment(filters.deliveryDateRange[1]).toISOString(),
        ]
        : [],
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    dispatch(getAllRidersAction(params))
      .unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [filters, pagination, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const handleStatusChange = (record, newStatus) => {
    Modal.confirm({
      title: 'Are you sure?',
      content: `Are you sure to perform this action? Mark status as ${newStatus}`,
      onOk: () => {
        // Call API to update the status
        dispatch(
          updateRiderStatusAction({ id: record._id, status: newStatus }) // Replace with the actual action for updating status
        )
          .unwrap()
          .then((resp) => {

            if (resp.success) {
              notificationController.success({
                message: `Status updated to ${newStatus}`,
              });
              fetchData();
            }
            else {
              notificationController.success({
                message: `${resp?.message || "Something went wrong"}`,
              });
            }
          })
          .catch((error) => {
            notificationController.error({
              message: error.message || 'Failed to update status',
            });
          });
      },
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'First Name',
      dataIndex: 'firstName',
      render: (text, record) => <span>{record?.firstName || 'Not available'}</span>,
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      render: (text, record) => <span>{record?.lastName || 'Not available'}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (text, record) => <span>{record?.email || 'Not available'}</span>,
    },
    {
      title: 'Mobile Number',
      dataIndex: 'phone',
      render: (text, record) => <span>{record?.phone || 'Not available'}</span>,
    },
    {
      title: 'Account Status',
      dataIndex: 'status',
      render: (text, record) => <span>{text}</span>,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: '15%',
      render: (text: string, record: any) => (
        <div style={{ display: 'block', justifyContent: 'space-between' }}>
          <label title='Account Status'>Status</label>
          <Select

            defaultValue={record.status}
            style={{ marginLeft: '5px', width: 120 }}
            onChange={(value) => handleStatusChange(record, value)}
          >
            <Option value="Active">Active</Option>
            <Option value="Suspended">Suspended</Option>
          </Select>
        </div>

      ),
    },
  ];

  return (
    <>
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setFilters({
            clientName: '',
            deliveryDateRange: [],
            deliveryStatus: '',
          });
          setPagination({ current: 1, pageSize: 20 });
          setTableKey(tableKey + 1);
        }}
      >
        Clear Filters
      </Button>
      <BaseTable
        key={tableKey}
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />
    </>
  );
};
